import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

type ColorMode = 'light' | 'dark';

interface ColorModeContextType {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>('light');

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#000' : '#fff',
          },
          secondary: {
            main: '#d32f2f',
          },
          background: {
            default: mode === 'light' ? '#fff' : '#121212',
            paper: mode === 'light' ? '#fff' : '#1a1a1a',
          },
        },
        typography: {
          fontFamily: '"Source Sans Pro", sans-serif',
          h1: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '2.5rem',
            fontWeight: 700,
          },
          h2: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '2rem',
            fontWeight: 600,
          },
          h3: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.75rem',
            fontWeight: 600,
          },
          h4: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.5rem',
            fontWeight: 600,
          },
          h5: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.25rem',
            fontWeight: 600,
          },
          h6: {
            fontFamily: '"Playfair Display", serif',
            fontSize: '1rem',
            fontWeight: 600,
          },
          body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
          },
          body2: {
            fontSize: '0.875rem',
            lineHeight: 1.43,
          },
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: 'none',
                border: '1px solid',
                borderColor: mode === 'light' ? '#e0e0e0' : '#333',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 0,
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ColorModeContext.Provider>
  );
}; 